import { useState, useMemo } from 'react';
import { useInventoryItems } from '@/hooks/api-hooks';
import InventoryItemsList from '@/components/inventory-items-list';
import SearchInput from '@/components/search-input';
import type { InventoryItem } from '@turizi/shared';
import { multiWordSearch } from '@/utils/search-utils';
import { SkeletonList } from '@/components/snippets';


interface Props {
    onSelect?: (item: InventoryItem) => void;
    onRemove?: (item: InventoryItem) => void;
    saving: boolean;
    loadingIds?: number[];
    selected?: InventoryItem['id'][];
    onEditItem?: (item: InventoryItem) => void;

    onCreateChild?: (parent: InventoryItem) => void;
}

export default function InventoryListWithFilters({
    onSelect,
    onRemove,
    saving,
    selected,
    loadingIds,
    onEditItem,
    onCreateChild,
}: Props): JSX.Element {
    const [searchText, setSearchText] = useState('');
    const {
        data,
        isLoading
    } = useInventoryItems();
    const inventoryItems = (data?.data || [])
        .sort((a, b) => a.name.localeCompare(b.name));

    const filteredItems = useMemo(() => {
        if (!searchText.trim()) return inventoryItems;
        const searchLower = searchText.toLowerCase().trim();

        // Build parent-child relationships
        const childrenMap = new Map<number, number[]>();
        const parentMap = new Map<number, number>();

        inventoryItems.forEach(item => {
            if (item.parent_id) {
                parentMap.set(item.id, item.parent_id);
                if (!childrenMap.has(item.parent_id)) {
                    childrenMap.set(item.parent_id, []);
                }
                childrenMap.get(item.parent_id)!.push(item.id);
            }
        });

        const itemMatches = (item: InventoryItem) => {
            return (
                multiWordSearch(item.name, searchLower) ||
                multiWordSearch(item.business?.name, searchLower)
            );
        };

        // Get all descendants of an item
        const getAllDescendants = (itemId: number): number[] => {
            const descendants: number[] = [];
            const children = childrenMap.get(itemId) || [];
            children.forEach(childId => {
                descendants.push(childId);
                descendants.push(...getAllDescendants(childId));
            });
            return descendants;
        };

        // Get all ancestors of an item
        const getAllAncestors = (itemId: number): number[] => {
            const ancestors: number[] = [];
            let currentId = itemId;
            while (parentMap.has(currentId)) {
                const parentId = parentMap.get(currentId)!;
                ancestors.push(parentId);
                currentId = parentId;
            }
            return ancestors;
        };

        // Collect IDs to include
        const idsToInclude = new Set<number>();

        inventoryItems.forEach(item => {
            if (itemMatches(item)) {
                // Include the matching item
                idsToInclude.add(item.id);
                // Include all its descendants
                getAllDescendants(item.id).forEach(id => idsToInclude.add(id));
                // Include all its ancestors (to maintain tree structure)
                getAllAncestors(item.id).forEach(id => idsToInclude.add(id));
            }
        });

        return inventoryItems.filter(item => idsToInclude.has(item.id));
    }, [inventoryItems, searchText]);

    return (
        <div>
            <SearchInput
                className="mb-2"
                size="md"
                onSearch={(text) => setSearchText(text)}
            />
            {
                isLoading
                    ?
                    <SkeletonList />
                    :
                    <InventoryItemsList
                        items={filteredItems}
                        onSelect={onSelect}
                        onRemove={onRemove}
                        saving={saving}
                        loadingIds={loadingIds}
                        selected={selected}
                        onEditItem={onEditItem}
                        onCreateChild={onCreateChild}
                    />
            }
        </div>
    );
}